class AdocHeroSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-adoc-slider-track]');
    this.slides = Array.from(this.querySelectorAll('[data-adoc-slider-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-adoc-slider-dot]'));
    this.previousButtons = Array.from(this.querySelectorAll('[data-adoc-slider-previous]'));
    this.nextButtons = Array.from(this.querySelectorAll('[data-adoc-slider-next]'));
    this.index = 0;
    this.autoplayTimer = undefined;
    this.currentVideo = undefined;
    this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1;
    this.autoplayDelay = Number.parseInt(this.dataset.autoplayDelay || '5000', 10);

    this.previousButtons.forEach((button) => button.addEventListener('click', () => this.previous()));
    this.nextButtons.forEach((button) => button.addEventListener('click', () => this.next()));
    this.dots.forEach((button, index) => button.addEventListener('click', () => this.goTo(index)));

    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', () => this.startAutoplay());
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);
    // Si el navegador bloqueo el autoplay (Low Power Mode / ahorro de datos),
    // reintentar en la primera interaccion del usuario.
    this.addEventListener('pointerdown', () => this.syncCurrentVideo(), { once: true });

    // Re-sincronizar el video visible cuando el layout ya es definitivo y si
    // cambia el breakpoint (rotacion del dispositivo / resize): reproduce el
    // correcto y pausa al gemelo.
    window.addEventListener('load', () => this.syncCurrentVideo(), { once: true });
    this.mobileMedia = window.matchMedia('(max-width: 749px)');
    if (this.mobileMedia.addEventListener) {
      this.mobileMedia.addEventListener('change', () => this.syncCurrentVideo());
    }

    this.update(false);
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
  }

  handleVideoEnded = () => {
    this.next();
  };

  handleVisibilityChange = () => {
    if (document.hidden) {
      this.stopAutoplay();
    } else {
      this.startAutoplay();
    }
  };

  handleBlockSelect = (event) => {
    const slide = event.target.closest?.('[data-adoc-slider-slide]');
    if (!slide || !this.contains(slide)) return;

    const index = this.slides.indexOf(slide);
    if (index >= 0) {
      this.stopAutoplay();
      this.goTo(index);
    }
  };

  previous() {
    this.goTo(this.index - 1);
  }

  next() {
    this.goTo(this.index + 1);
  }

  goTo(index) {
    if (!this.slides.length) return;

    const maxIndex = this.slides.length - 1;
    if (index < 0) {
      this.index = maxIndex;
    } else if (index > maxIndex) {
      this.index = 0;
    } else {
      this.index = index;
    }

    this.update();
    this.startAutoplay();
  }

  update(animate = true) {
    if (!this.track) return;

    this.track.style.transitionDuration = animate ? '' : '0ms';
    this.track.style.transform = `translate3d(${this.index * -100}%, 0, 0)`;

    this.slides.forEach((slide, index) => {
      const isCurrent = index === this.index;
      slide.setAttribute('aria-hidden', `${!isCurrent}`);
      slide.toggleAttribute('data-current', isCurrent);

      if (!isCurrent) {
        slide.querySelectorAll('video').forEach((video) => {
          video.pause();
          video.currentTime = 0;
        });
      }
    });

    // El video del slide visible debe reproducirse SIEMPRE (aunque el
    // movimiento automatico del carrusel este apagado); el gemelo oculto
    // (desktop/mobile) se pausa para no gastar red/CPU.
    this.syncCurrentVideo();

    this.dots.forEach((dot, index) => {
      dot.setAttribute('aria-selected', `${index === this.index}`);
    });

    if (!animate) {
      requestAnimationFrame(() => {
        this.track.style.transitionDuration = '';
      });
    }
  }

  startAutoplay() {
    this.stopAutoplay();
    if (!this.autoplayEnabled || document.hidden) return;

    const currentVideo = this.getCurrentVideo();

    if (!currentVideo) {
      this.autoplayTimer = window.setTimeout(() => this.next(), this.autoplayDelay);
      return;
    }

    this.currentVideo = currentVideo;
    currentVideo.loop = false;
    currentVideo.removeAttribute('loop');

    if (currentVideo.ended || (Number.isFinite(currentVideo.duration) && currentVideo.currentTime >= currentVideo.duration - 0.25)) {
      currentVideo.currentTime = 0;
    }

    currentVideo.addEventListener('ended', this.handleVideoEnded, { once: true });

    const playPromise = currentVideo.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {
        this.autoplayTimer = window.setTimeout(() => this.next(), this.autoplayDelay);
      });
    }
  }

  stopAutoplay() {
    window.clearTimeout(this.autoplayTimer);
    this.autoplayTimer = undefined;

    if (this.currentVideo) {
      this.currentVideo.removeEventListener('ended', this.handleVideoEnded);
      // El carrusel queda pausado (hover/focus): restaurar el loop para que
      // el video no muera congelado en el ultimo frame al terminar.
      this.currentVideo.loop = true;
      this.currentVideo.setAttribute('loop', '');
      this.currentVideo = undefined;
    }
  }

  syncCurrentVideo() {
    const slide = this.slides[this.index];
    if (!slide) return;

    const visibleVideo = this.getCurrentVideo();

    slide.querySelectorAll('video').forEach((video) => {
      if (video !== visibleVideo) {
        video.pause();
        return;
      }

      if (video.ended) {
        video.currentTime = 0;
      }

      const playPromise = video.play();
      playPromise?.catch?.(() => {});
    });
  }

  getCurrentVideo() {
    const slide = this.slides[this.index];
    if (!slide) return undefined;

    const videos = Array.from(slide.querySelectorAll('video'));
    if (!videos.length) return undefined;

    // Decidir por breakpoint (mismo corte que el CSS: 749px), NO por estilos
    // computados: este script puede correr antes de que el stylesheet aplique
    // y en ese instante ambos videos "se ven" visibles — eso hacia que el
    // gemelo equivocado tambien se reprodujera y descargara su stream.
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const wantedClass = isMobile ? 'adoc-hero-slider__video--mobile' : 'adoc-hero-slider__video--desktop';

    return videos.find((video) => video.classList.contains(wantedClass)) || videos[0];
  }
}

if (!customElements.get('adoc-hero-slider')) {
  customElements.define('adoc-hero-slider', AdocHeroSlider);
}
